import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import ReadLayerQueryContext from "./ReadLayerQueryContext";
import { ReadLayerQuery, ReadLayerQueryFilter } from "api/services";
import { ReactNode, useEffect, useState } from "react";

interface ReadLayerProps {
  indexName: string;
  serviceName: string;
  sortOnColumn: string;
  sortAscending?: boolean;
  facets?: Array<string>;
  facetSize?: number;
  children?: ReactNode;
  filters?: Array<ReadLayerQueryFilter>;
  filterRewrite?: (filter: ReadLayerQueryFilter) => ReadLayerQueryFilter;
  disableQuery?: boolean;
  onUpdate?: (query: ReadLayerQuery) => void;
  subscribeToPropUpdates?: boolean;
}

const getDefaultQuery = (props: ReadLayerProps) => {
  const {
    indexName,
    serviceName,
    sortOnColumn,
    sortAscending,
    facets,
    facetSize,
    filters
  } = props;

  return {
    searchTerm: "",
    searchTermColumns: [],
    index: indexName,
    service: serviceName,
    page: 1,
    pageSize: 10,
    sortOnColumn: sortOnColumn,
    sortAscending: sortAscending ?? true,
    filters: filters ?? [],
    facets: !facets
      ? []
      : facets.map(x => {
          return { column: x, size: facetSize ? facetSize : 10 };
        })
  };
};

export const ReadLayerWrapper = (props: ReadLayerProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const search = searchParams.get("search");

  const { children, filterRewrite, onUpdate, disableQuery } = props;

  let defaultQuery = getDefaultQuery(props);

  if (search) {
    try {
      const urlQuery = JSON.parse(decodeURIComponent(escape(atob(search))));
      defaultQuery = urlQuery;
    } catch (e) {
      // Fall back on the default query if deserialization fails if the query param was invalid.
    }
  }

  const updateQuery = (query: ReadLayerQuery) => {
    const json = JSON.stringify(query);
    if (query.filters.length == 0 && query.searchTerm === "")
      navigate(location.pathname);
    else {
      let params = "?search=" + btoa(unescape(encodeURIComponent(json)));

      const cols = searchParams.get("cols");

      if (cols) {
        params += "&cols=" + cols;
      }

      const stats = searchParams.get("stats");

      if (stats) {
        params += "&stats=" + stats;
      }

      navigate(location.pathname + params, {
        replace: true
      });
    }

    if (onUpdate) onUpdate(query);
  };

  useEffect(() => {
    if (onUpdate) onUpdate(defaultQuery);
  }, []);

  return (
    <ReadLayerQueryContext
      defaultQuery={defaultQuery}
      filterRewrite={filterRewrite}
      disableQuery={disableQuery ?? false}
      onUpdate={updateQuery}
      subscribeToPropUpdates={props.subscribeToPropUpdates ?? false}
    >
      {children}
    </ReadLayerQueryContext>
  );
};

export const ReadLayerStateWrapper = (props: ReadLayerProps) => {
  const { children, filterRewrite, disableQuery } = props;
  const defaultQuery = getDefaultQuery(props);
  const [query, setQuery] = useState<Required<ReadLayerQuery>>(defaultQuery);

  useEffect(() => {
    setQuery(defaultQuery);
  }, [props.filters]);

  const updateQuery = (updatedQuery: ReadLayerQuery) => {
    setQuery(Object.assign({}, defaultQuery, updatedQuery));
  };
  return (
    <ReadLayerQueryContext
      defaultQuery={query}
      onUpdate={updateQuery}
      filterRewrite={filterRewrite}
      disableQuery={disableQuery ?? false}
      subscribeToPropUpdates={props.subscribeToPropUpdates ?? false}
    >
      {children}
    </ReadLayerQueryContext>
  );
};
